import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';
import { Container, Typography, TextField, Button, Dialog, DialogTitle, DialogContent, Box, DialogActions } from "@mui/material";
import { motion } from "framer-motion";

import emailjs from 'emailjs-com';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { formatPhoneNumber } from '../utils/phoneFormatter';

const LeadCaptureForm = () => {
    const navigate = useNavigate();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subdomain: '',
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'subdomain') {
            let subdomainPrefix = value
                .replace(/^https?:\/\//i, '')
                .replace(/\.nasradius\.com.*$/i, '')
                .trim();
            setFormData((prevData) => ({ ...prevData, [name]: subdomainPrefix }));
        } else {
            setFormData((prevData) => ({ ...prevData, [name]: value }));
        }
    };

    const handlePhoneBlur = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        if (formatted !== e.target.value) {
            setFormData((prevData) => ({ ...prevData, phone: formatted }));
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const sendEmail = async () => {
        const fullSubdomain = formData.subdomain ? `https://${formData.subdomain}.nasradius.com` : '';

        return emailjs.send(
          'service_u5dkan9',
          'template_4souqbn',
          {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            subdomain: fullSubdomain,
          },
          'ht9UhP00cZq3k6-SH'
        );
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        handleStartTrial();
    };

    const handleStartTrial = (e) => {
        if (e) {
            e.preventDefault();
        }
        if (!formData.name || !formData.email || !formData.phone || !formData.subdomain) {
            alert('Please fill in all required fields.');
            return;
        }

        const formattedPhone = formatPhoneNumber(formData.phone);

        sendEmail()
            .then(() => {
                console.log('Email sent successfully');
            })
            .catch((err) => {
                console.error('Failed to send email:', err);
            });

        const params = new URLSearchParams();
        if (formData.name) params.append('name', formData.name);
        if (formData.email) params.append('email', formData.email);
        if (formattedPhone) params.append('phone', formattedPhone);
        if (formData.subdomain) params.append('subdomain', formData.subdomain);

        navigate(`/onboarding?${params.toString()}`);
    };

    return (
        <Box
            sx={{
                backgroundColor: '#f5f5f7',
                py: { xs: 10, md: 18 },
            }}
            id="contact"
        >
            <Container maxWidth="sm">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
                >
                    <Typography
                        variant="h2"
                        align="center"
                        sx={{
                            fontWeight: 700,
                            letterSpacing: '-0.03em',
                            background: 'linear-gradient(135deg, #19b594 0%, #0b74c1 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            mb: 2,
                        }}
                    >
                        Start Your Free Trial
                    </Typography>
                    <Typography
                        align="center"
                        sx={{
                            color: 'rgba(29,29,31,0.65)',
                            mb: { xs: 4, md: 6 },
                            fontSize: { xs: '0.95rem', md: '1.05rem' },
                            lineHeight: 1.6,
                        }}
                    >
                        Fill out the form below to start your free trial. You will be redirected to the onboarding page to complete the setup.
                        <Box
                            component="span"
                            sx={{
                                display: 'block',
                                color: '#19b594',
                                fontWeight: 500,
                                fontSize: { xs: '0.85rem', md: '0.9rem' },
                                mt: 1.5,
                            }}
                        >
                            Get a free subdomain for your ISP business.
                        </Box>
                    </Typography>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
                >
                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                            backgroundColor: '#ffffff',
                            borderRadius: 4,
                            p: { xs: 3, md: 5 },
                            border: '1px solid rgba(0,0,0,0.06)',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                        }}
                    >
                        <TextField
                            label="Name"
                            name="name"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            required
                            onChange={handleChange}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#0b74c1',
                                    },
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#0b74c1',
                                },
                            }}
                        />
                        <TextField
                            label="Email"
                            name="email"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            required
                            onChange={handleChange}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#0b74c1',
                                    },
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#0b74c1',
                                },
                            }}
                        />
                        <TextField
                            label="Phone"
                            name="phone"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            value={formData.phone}
                            onChange={handleChange}
                            onBlur={handlePhoneBlur}
                            placeholder="0712345678 or +254712345678"
                            helperText="Enter your phone number (we'll automatically add country code)"
                            required
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#0b74c1',
                                    },
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#0b74c1',
                                },
                            }}
                        />
                        <TextField
                            label="Preferred Subdomain"
                            name="subdomain"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            required
                            placeholder="smartnetworks"
                            value={formData.subdomain}
                            onChange={handleChange}
                            helperText={
                                formData.subdomain ? (
                                    <Typography
                                        component="span"
                                        sx={{
                                            color: '#19b594',
                                            fontWeight: 500,
                                            fontSize: { xs: '0.8rem', md: '0.85rem' },
                                        }}
                                    >
                                        Your domain: https://{formData.subdomain}.nasradius.com
                                    </Typography>
                                ) : ''
                            }
                            InputProps={{
                                startAdornment: (
                                    <Typography sx={{ color: 'rgba(29,29,31,0.4)', fontSize: { xs: '0.85rem', md: '0.9rem' }, whiteSpace: 'nowrap' }}>
                                        https://
                                    </Typography>
                                ),
                                endAdornment: (
                                    <Typography sx={{ color: 'rgba(29,29,31,0.4)', fontSize: { xs: '0.85rem', md: '0.9rem' }, whiteSpace: 'nowrap' }}>
                                        .nasradius.com
                                    </Typography>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#0b74c1',
                                    },
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#0b74c1',
                                },
                            }}
                        />
                        <Button
                            variant="contained"
                            size="large"
                            type="submit"
                            fullWidth
                            sx={{
                                mt: 3,
                                fontWeight: 500,
                                letterSpacing: '-0.005em',
                                fontSize: { xs: '0.95rem', md: '1.05rem' },
                                py: { xs: 1.5, md: 1.75 },
                                borderRadius: 980,
                                background: 'linear-gradient(135deg, #0a66bb 0%, #19b594 100%)',
                                color: '#ffffff',
                                textTransform: 'none',
                                boxShadow: '0 8px 24px rgba(10,102,187,0.25)',
                                transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #0b74c1 0%, #219cac 100%)',
                                    transform: 'scale(1.02)',
                                    boxShadow: '0 12px 32px rgba(10,102,187,0.35)',
                                },
                            }}
                        >
                            Start Your Free Trial
                        </Button>
                    </Box>
                </motion.div>

                <Dialog
                    open={dialogOpen}
                    onClose={handleDialogClose}
                    sx={{
                        '& .MuiPaper-root': {
                            borderRadius: 3,
                            p: 2,
                            maxWidth: '420px',
                        },
                    }}
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#2e7d32' }}>
                        <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 28 }} />
                        Form Submitted Successfully
                    </DialogTitle>
                    <DialogContent>
                        <Typography sx={{ color: 'rgba(29,29,31,0.7)', fontSize: '0.95rem', lineHeight: 1.6 }}>
                            Thank you, <strong>{formData.name}</strong>! Your request has been received. We will contact you soon.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleDialogClose}
                            variant="contained"
                            sx={{
                                borderRadius: 980,
                                backgroundColor: '#2e7d32',
                                textTransform: 'none',
                                px: 4,
                                '&:hover': { backgroundColor: '#1b5e20' },
                            }}
                        >
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};

export default LeadCaptureForm;
